"use client";

import React from "react";
import { Wrench, CircleCheck, TriangleAlert, Settings, ShieldCheck, ArrowRight, Clock, PenTool } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const commonIssues = [{
  id: "issue-1",
  title: "اضطراب و استرس مداوم",
  content: "احساس نگرانی همیشگی، تپش قلب و افکار مزاحم که مانع از تمرکز و آرامش در زندگی روزمره شما می‌شوند."
}, {
  id: "issue-2",
  title: "وسواس فکری و عملی",
  content: "تکرار ناخواسته افکار یا رفتارهایی که زمان زیادی از شما می‌گیرند و باعث ایجاد فشار روانی شدید می‌شوند."
}, {
  id: "issue-3",
  title: "چالش‌های والد و کودک",
  content: "دشواری در برقراری ارتباط موثر با فرزندان، لجبازی‌های کودکانه یا بحران‌های دوره نوجوانی که نیاز به راهنمایی تخصصی دارند."
}];
const processSteps = [{
  title: "ارزیابی اولیه",
  description: "بررسی تاریخچه فردی و شناسایی ریشه‌های اصلی چالش‌های روانشناختی شما.",
  icon: <Settings className="size-5 text-primary" />
}, {
  title: "تدوین طرح درمان",
  description: "طراحی یک مسیر اختصاصی بر اساس نیازهای منحصر به فرد و اهداف شخصی شما.",
  icon: <PenTool className="size-5 text-primary" />
}, {
  title: "جلسات مشاوره",
  description: "برگزاری جلسات منظم در فضایی امن و بدون قضاوت برای کار بر روی تغییرات.",
  icon: <Wrench className="size-5 text-primary" />
}, {
  title: "پایش و تثبیت",
  description: "ارزیابی پیشرفت‌های حاصل شده و آموزش مهارت‌هایی برای حفظ سلامت روان در بلندمدت.",
  icon: <CircleCheck className="size-5 text-primary" />
}];
const specifications = [{
  feature: "مدت زمان هر جلسه",
  value: "۴۵ تا ۶۰ دقیقه"
}, {
  feature: "نحوه برگزاری",
  value: "آنلاین (تصویری)"
}, {
  feature: "زبان‌های مشاوره",
  value: "فارسی، انگلیسی و عربی"
}, {
  feature: "رویکرد درمانی",
  value: "CBT، طرحواره درمانی و درمان‌های موج سوم"
}, {
  feature: "گروه سنی",
  value: "کودک، نوجوان و بزرگسال"
}];
export default function Onlineappointmentbooking() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: leftColRef,
    isVisible: isLeftColVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightColRef,
    isVisible: isRightColVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="252460" id="onlineappointmentbooking" className="py-20 bg-background text-foreground overflow-hidden">
      <div className="page-container">
        {/* Section Header */}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="secondary" className="mb-4 text-primary bg-accent">
            خدمات تخصصی روانشناسی
          </Badge>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default mb-6 text-foreground">
            فرآیند مشاوره و رزرو نوبت
          </h1>
          <p className="text-lg text-muted-foreground font-default">
            دکتر مهرنوش اسماعیلی با بیش از ۱۰ سال تجربه بالینی، در فضایی آرام و حرفه‌ای همراه شماست تا مسیر بهبود سلامت روان خود را با اطمینان طی کنید.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left Column: Interactive Elements & Steps */}
          <div ref={leftColRef} className={`space-y-10 transition-all duration-700 delay-100 ${isLeftColVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            {/* Common Issues Accordion */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <TriangleAlert className="size-6 text-primary" />
                <h3 className="text-2xl font-bold font-default text-foreground">
                  چالش‌هایی که با هم حل می‌کنیم
                </h3>
              </div>
              <Accordion type="single" collapsible className="w-full bg-card rounded-lg border border-border px-4 shadow-sm">
                {commonIssues.map((issue, index) => <AccordionItem key={issue.id} value={issue.id} data-index={index}>
                    <AccordionTrigger className="text-base font-semibold font-default hover:text-primary transition-colors">
                      {issue.title}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground font-default leading-relaxed">
                      {issue.content}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>

            <Separator />

            {/* Treatment Steps Grid */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Wrench className="size-6 text-primary" />
                <h3 className="text-2xl font-bold font-default text-foreground">
                  مراحل درمان و همراهی
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {processSteps.map((step, index) => <Card key={index} data-index={index} className="border-border shadow-md hover:shadow-lg transition-shadow bg-card rounded-lg">
                    <CardHeader className="pb-2">
                      <div className="mb-2 bg-accent w-10 h-10 rounded-lg flex items-center justify-center">
                        {step.icon}
                      </div>
                      <CardTitle className="text-lg font-bold font-default text-foreground">
                        {index + 1}. {step.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground font-default">
                        {step.description}
                      </p>
                    </CardContent>
                  </Card>)}
              </div>
            </div>
          </div>

          {/* Right Column: Imagery & Technical Specs */}
          <div ref={rightColRef} className={`space-y-10 transition-all duration-700 delay-200 ${isRightColVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            {/* Visual Card */}
            <div className="relative rounded-lg overflow-hidden border border-border shadow-xl group">
              <div className="absolute inset-0 bg-foreground/5 group-hover:bg-transparent transition-colors duration-500 z-10" />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1684933/img/psychology_consultation_session.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1684933/img/psychology_consultation_session.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1684933/img/psychology_consultation_session.png-wvc-sizes" alt="دکتر مهرنوش اسماعیلی در حال مشاوره" className="w-full h-[300px] object-cover transform group-hover:scale-105 transition-transform duration-700" loading="lazy" />
              <div className="absolute bottom-4 left-4 z-20 flex gap-2">
                <Badge className="bg-background/90 text-foreground backdrop-blur-sm border-none shadow-sm rounded-lg">
                  <ShieldCheck className="size-3 mr-1 text-primary" />
                  تخصص و تجربه
                </Badge>
                <Badge className="bg-background/90 text-foreground backdrop-blur-sm border-none shadow-sm rounded-lg">
                  <Clock className="size-3 mr-1 text-primary" />
                  نوبت‌دهی سریع
                </Badge>
              </div>
            </div>

            {/* Specifications Table */}
            <Card className="border-border shadow-md bg-card overflow-hidden rounded-lg">
              <CardHeader className="bg-accent/30 border-b border-border pb-4">
                <CardTitle className="text-xl font-bold font-default text-foreground flex items-center gap-2">
                  <Settings className="size-5 text-primary" />
                  جزئیات جلسات مشاوره
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableBody>
                    {specifications.map((spec, index) => <TableRow key={index} data-index={index} className="hover:bg-accent/20">
                        <TableCell className="font-semibold font-default text-foreground py-4 pr-6 text-right">
                          {spec.feature}
                        </TableCell>
                        <TableCell className="text-muted-foreground font-default py-4 pl-6 text-left">
                          {spec.value}
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Quick Action Box */}
            <div className="bg-accent/20 rounded-lg p-8 text-center border border-border/50 shadow-sm">
              <h4 className="text-xl font-bold font-default text-foreground mb-3">
                آماده‌اید اولین قدم را برای آرامش خود بردارید؟
              </h4>
              <p className="text-muted-foreground font-default mb-6">
                سلامت روان شما ارزشمندترین دارایی شماست. همین حالا نوبت خود را رزرو کنید.
              </p>
              <Link to="/BookAppointmentPage">
                <Button size="lg" className="w-full sm:w-auto group rounded-lg bg-primary text-primary-foreground hover:opacity-90">
                  رزرو نوبت آنلاین
                  <ArrowRight className="mr-2 size-4 group-hover:-translate-x-1 transition-transform rotate-180" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}